import { Checkbox } from '@v2/components/ui/checkbox';
import { cn } from '@v2/lib/utils';
import * as React from 'react';
import { Select, SelectContent, SelectClearTrigger } from '../Select';
import { SelectSearch } from './SelectSearch';
import { InfiniteScroll } from '../../../components/InfiniteScroll/InfiniteScroll';
import { FilterInputHandle } from './SelectSearch';
import { Check } from 'lucide-react';
import { LabelWithHighlight } from '../../../data-display/labels/LabelWithHighlight/LabelWithHighlight';
import { useFilterContext } from '../../../controls/FilterContext/FilterContext';
import { ModalButtons } from '@v2/components/overlay/ModalButtons/ModalButtons';
import { Label } from '@v2/components/ui/label';

export interface SelectOption {
  value: string;
  label: string;
}

export interface GenericSelectProps {
  options: SelectOption[];
  onCancel: () => void;
  onChange: (values: string | string[]) => void;
  defaultValue?: string | string[];
  placeholder?: string;
  isMultiSelect?: boolean;
  maxAllowed?: number;
  showSearch?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  showApplyButton?: boolean;
  onApply?: (values: string[] | string) => void;
  className?: string;
  applyOnClick?: boolean;
  onInputChange?: (values: string) => void;
  filter?: string;
  triggerComponent?: React.ReactNode;
  disabled?: boolean;
  triggerClassName?: string;
  triggerIcon?: React.ReactNode;
  closeOnApply?: boolean;
}

export function renderValuesLabel(
  values: string | string[],
  options: SelectOption[],
  placeholder = 'Select options'
) {
  const valuesArray = Array.isArray(values) ? values : [values];

  if (valuesArray.length === 0) {
    return placeholder;
  }

  const selectedOptions = options.filter((option) =>
    valuesArray.includes(option.value)
  );

  if (Array.isArray(values)) {
    return values.length > 1
      ? `${values.length} selected`
      : selectedOptions[0]?.label || placeholder;
  }

  return selectedOptions[0]?.label || placeholder;
}

export function GenericSelect({
  options,
  onCancel,
  onChange,
  defaultValue,
  placeholder = 'Select options',
  isMultiSelect = false,
  maxAllowed,
  showSearch = true,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  showApplyButton = false,
  onApply,
  className,
  applyOnClick = false,
  onInputChange,
  triggerComponent,
  disabled = false,
  triggerClassName = '',
  triggerIcon,
  closeOnApply = false,
  filter: controlledFilter
}: GenericSelectProps) {
  const defaultValueArray = React.useMemo(() => {
    if (isMultiSelect) {
      if (Array.isArray(defaultValue)) {
        return defaultValue;
      }
      if (defaultValue) {
        return [defaultValue];
      }
      return [];
    }
    return defaultValue ?? '';
  }, [defaultValue, isMultiSelect]);

  const [selectedValues, setSelectedValues] = React.useState<string | string[]>(
    defaultValueArray
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const searchRef = React.useRef<FilterInputHandle>(null);

  const { filter: contextFilter } = useFilterContext();
  const filter = controlledFilter || contextFilter;

  const depDefaultValue = JSON.stringify(defaultValueArray);
  const depDefaultValueArray = JSON.stringify(defaultValueArray);

  React.useEffect(() => {
    if (defaultValue) {
      setSelectedValues(defaultValueArray);
    }
  }, [depDefaultValue, depDefaultValueArray]);

  const filteredOptions = React.useMemo(() => {
    return options.filter((option) => {
      return (
        option?.label?.toLowerCase().includes(filter.toLowerCase()) ||
        option?.value?.toLowerCase().includes(filter.toLowerCase())
      );
    });
  }, [options, filter]);

  React.useEffect(() => {
    if (isOpen && showSearch) {
      searchRef.current?.focus();
    } else if (!isOpen && showSearch) {
      searchRef.current?.resetFilter();
    }
  }, [isOpen, showSearch]);

  const handleValueChange = (value: string) => {
    if (isMultiSelect) {
      const currentValues = Array.isArray(selectedValues) ? selectedValues : [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      if (!maxAllowed || newValues.length <= maxAllowed) {
        setSelectedValues(newValues);
        if (applyOnClick) {
          onChange(newValues);
        } else {
          onChange(newValues);
        }
      }
    } else {
      setSelectedValues(value);
      if (applyOnClick) {
        onChange(value);
        if (onApply) {
          handleApply();
        }
      } else {
        onChange(value);
      }
    }
  };

  const handleApply = () => {
    if (isMultiSelect && onApply) {
      onApply(Array.isArray(selectedValues) ? selectedValues : []);
    } else if (onApply) {
      onApply(selectedValues);
    }

    if (closeOnApply) {
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setSelectedValues(isMultiSelect ? [] : '');
    onCancel();
  };

  const scrollRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className={className}>
      <Select disabled={disabled} onOpenChange={setIsOpen} open={isOpen}>
        {triggerComponent ? (
          React.cloneElement(triggerComponent as React.ReactElement, {
            children: renderValuesLabel(selectedValues, options, placeholder)
          })
        ) : (
          <SelectClearTrigger
            className={cn(
              'flex max-w-[280px] flex-row items-center justify-start gap-1 pl-0',
              triggerClassName
            )}
          >
            {triggerIcon}
            {renderValuesLabel(selectedValues, options, placeholder)}
          </SelectClearTrigger>
        )}
        <div>
          <SelectContent
            ref={scrollRef}
            variant="dropdown"
            position="popper"
            side="bottom"
            TopElement={
              showSearch ? (
                <SelectSearch
                  ref={searchRef}
                  onInputChange={onInputChange}
                  filter={filter}
                />
              ) : undefined
            }
            onPointerLeave={(event) => event.preventDefault()}
            onPointerMove={(event) => event.preventDefault()}
            scrollViewportClassname="px-0"
            BottomElement={
              <div className="flex h-[44px] select-none items-center">
                <ModalButtons
                  onApply={
                    showApplyButton && isMultiSelect && !applyOnClick
                      ? handleApply
                      : undefined
                  }
                  onCancel={handleClear}
                />
              </div>
            }
          >
            {filteredOptions.map((option) => {
              const isSelected =
                (Array.isArray(selectedValues) &&
                  selectedValues.includes(option.value)) ||
                selectedValues === option.value;
              return (
                <div
                  key={option.value}
                  className="group relative flex cursor-pointer items-center px-4 hover:bg-muted active:bg-muted"
                  onClick={() => {
                    handleValueChange(option.value);
                  }}
                >
                  {isMultiSelect ? (
                    <Checkbox
                      checked={isSelected}
                      className={'pointer-events-none'}
                    />
                  ) : isSelected ? (
                    <Check className="h-4 w-4 text-foreground pointer-events-none" />
                  ) : null}

                  <Label
                    className={cn(
                      'flex-1 cursor-pointer py-2 text-sm pointer-events-none'
                    )}
                  >
                    <LabelWithHighlight
                      label={option.label}
                      highlight={filter}
                    />
                  </Label>
                </div>
              );
            })}
            <InfiniteScroll
              hasNextPage={!!hasNextPage}
              fetchNextPage={fetchNextPage || (() => {})}
              isFetchingNextPage={!!isFetchingNextPage}
              offset={100}
              scrollRef={scrollRef}
            />
          </SelectContent>
        </div>
      </Select>
    </div>
  );
}
